import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Menu, ShoppingCart } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useAuth } from "@/lib/auth-context";
import { useCart } from "@/lib/cart";
import { buildNavTree, navHref, navItemsQueryOptions, type NavItem } from "@/lib/nav.queries";
import { cn } from "@/lib/utils";

// Nav text rides the theme's top-bar foreground token (not the page's
// muted/foreground colors) so the admin's "top bar text color" setting
// actually governs the nav; hovers tint with the same color for independence
// from the page surface tokens.
const pillClass =
  "shrink-0 whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm text-topbar-foreground/70 transition-colors hover:bg-topbar-foreground/10 hover:text-topbar-foreground sm:px-3";

const triggerClass =
  "h-auto shrink-0 whitespace-nowrap bg-transparent px-2.5 py-1.5 text-sm font-normal text-topbar-foreground/70 hover:bg-topbar-foreground/10 hover:text-topbar-foreground focus:bg-topbar-foreground/10 focus:text-topbar-foreground data-[state=open]:bg-topbar-foreground/10 data-[state=open]:text-topbar-foreground data-[state=open]:hover:bg-topbar-foreground/10 data-[state=open]:hover:text-topbar-foreground data-[state=open]:focus:bg-topbar-foreground/10 sm:px-3";

/** Current-page treatment: blue-grey pill, white text. */
const activePillClass =
  "bg-slate-600 text-white hover:bg-slate-600 hover:text-white font-medium";

/** Current-page treatment on page-surface lists (dropdown panels, drawer). */
const activeSurfaceClass =
  "bg-slate-600 text-white hover:bg-slate-600 hover:text-white font-medium";

function isExternalUrl(item: NavItem) {
  return !!item.external_url && /^https?:\/\//.test(item.external_url);
}

export function TopBar() {
  // Staff-only entry point: the Admin button exists only while a session is
  // live. `user` is null during the initial auth check and the instant
  // signOut() runs, so signed-out visitors (and prerender) never see it.
  const { user, loading } = useAuth();
  const signedIn = !loading && !!user;
  const { data: navItems, isLoading } = useQuery(navItemsQueryOptions());
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count: cartCount, setOpen: setCartOpen } = useCart();

  const ready = !isLoading && !!navItems;
  const visible = ready ? navItems!.filter((n) => n.is_active) : [];
  const { roots, children } = buildNavTree(visible);

  // An item is current when its resolved href matches the route. External links
  // (http/https) never match an app route.
  function isActive(item: NavItem) {
    const href = navHref(item);
    if (href === "#" || /^https?:\/\//.test(href)) return false;
    return href === pathname;
  }

  function groupActive(root: NavItem) {
    if (isActive(root)) return true;
    return (children.get(root.id) ?? []).some(isActive);
  }

  /** Anchor for a nav item; `active` adds the current-page treatment. */
  function navLink(
    item: NavItem,
    className: string,
    onNavigate?: () => void,
    activeClass: string = activePillClass,
  ) {
    const active = isActive(item);
    return (
      <a
        href={navHref(item)}
        target={item.open_in_new_tab ? "_blank" : undefined}
        rel={item.open_in_new_tab ? "noreferrer" : undefined}
        aria-current={active ? "page" : undefined}
        onClick={onNavigate}
        className={cn(className, active && activeClass)}
      >
        {item.label}
      </a>
    );
  }

  /** Panel entries for a group: the parent's own link first, if it has one. */
  function panelItems(root: NavItem) {
    const kids = children.get(root.id) ?? [];
    return navHref(root) !== "#" ? [root, ...kids] : kids;
  }

  return (
    <div className="sticky top-0 z-40 min-h-(--topbar-height) border-b bg-topbar/85 text-topbar-foreground backdrop-blur supports-[backdrop-filter]:bg-topbar/70">
      <div className="brand-gradient h-1 w-full" />
      {/* max-w-[96rem]: the 10-item horizontal nav needs ~1455px of content
          width — the site-standard max-w-7xl (1280px) cannot hold it. */}
      <div className="mx-auto flex max-w-[96rem] items-center gap-x-3 gap-y-2 px-3 py-2 sm:gap-4 sm:px-4">
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 2xl:hidden"
          aria-label="Open menu"
          onClick={() => setMobileOpen(true)}
        >
          <Menu className="h-5 w-5" />
        </Button>

        <Link to="/welcome" className="flex shrink-0 items-center gap-2">
          <img
            src="/logos/original/mii-logo-final.svg"
            alt="Medical Insight"
            width={40}
            height={40}
            className="h-10 w-10 shrink-0"
          />
          <span className="hidden text-sm font-semibold tracking-tight md:inline">
            Medical Insight
          </span>
        </Link>

        {/* Horizontal nav only on wide desktop (2xl+); with 10 root items —
            Gallery and The Beauty Show joined the original 8 — the header
            content measures ~1455px, so everything below 2xl uses the
            hamburger drawer to avoid horizontal overflow. */}
        <NavigationMenu className="hidden min-w-0 2xl:flex">
          <NavigationMenuList className="justify-start space-x-1">
            {roots.map((n) => {
              const kids = children.get(n.id) ?? [];
              if (kids.length === 0) {
                return (
                  <NavigationMenuItem key={n.id}>
                    <NavigationMenuLink asChild>{navLink(n, pillClass)}</NavigationMenuLink>
                  </NavigationMenuItem>
                );
              }
              return (
                <NavigationMenuItem key={n.id}>
                  <NavigationMenuTrigger
                    className={cn(
                      triggerClass,
                      groupActive(n) &&
                        "bg-slate-600 text-white font-medium hover:bg-slate-600 hover:text-white focus:bg-slate-600 focus:text-white data-[state=open]:bg-slate-600 data-[state=open]:text-white data-[state=open]:hover:bg-slate-600 data-[state=open]:hover:text-white data-[state=open]:focus:bg-slate-600",
                    )}
                  >
                    {n.label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="min-w-56 p-2">
                      {panelItems(n).map((c) => (
                        <li key={c.id}>
                          <NavigationMenuLink asChild>
                            {navLink(
                              c,
                              "block rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground",
                              undefined,
                              activeSurfaceClass,
                            )}
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              );
            })}
          </NavigationMenuList>
        </NavigationMenu>

        <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="relative shrink-0"
            aria-label={cartCount > 0 ? `Cart (${cartCount} items)` : "Cart"}
            onClick={() => setCartOpen(true)}
          >
            <ShoppingCart className="h-5 w-5" />
            {cartCount > 0 && (
              <Badge className="absolute -right-1 -top-1 h-5 min-w-5 justify-center rounded-full px-1 text-[10px] leading-none">
                {cartCount}
              </Badge>
            )}
          </Button>
          {signedIn && (
            <Button asChild variant="outline" size="sm" className="hidden md:inline-flex">
              <Link to="/admin">Admin</Link>
            </Button>
          )}
        </div>
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" aria-describedby={undefined} className="w-72 overflow-y-auto p-0">
          <SheetHeader className="border-b p-4">
            <SheetTitle className="text-left text-base">Menu</SheetTitle>
          </SheetHeader>
          <Accordion type="multiple" className="p-2">
            {roots.map((n) => {
              const kids = children.get(n.id) ?? [];
              if (kids.length === 0) {
                return (
                  <div key={n.id}>
                    {navLink(
                      n,
                      "block rounded-md px-2 py-3 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground",
                      () => setMobileOpen(false),
                      activeSurfaceClass,
                    )}
                  </div>
                );
              }
              return (
                <AccordionItem key={n.id} value={n.id} className="border-b-0">
                  <AccordionTrigger
                    className={cn(
                      // font-body: Radix wraps the trigger in an <h3>, which our
                      // theme styles set to the display font — without this the
                      // label renders in a different typeface than sibling links.
                      "px-2 py-3 font-body text-sm font-normal text-muted-foreground hover:no-underline",
                      groupActive(n) && "rounded-md bg-slate-600 text-white font-medium",
                    )}
                  >
                    {n.label}
                  </AccordionTrigger>
                  <AccordionContent className="pb-1 pt-0">
                    {panelItems(n).map((c) => (
                      <div key={c.id}>
                        {navLink(
                          c,
                          "block rounded-md py-2 pl-6 pr-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground",
                          () => setMobileOpen(false),
                          activeSurfaceClass,
                        )}
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
          {signedIn && (
            <div className="border-t p-4">
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link to="/admin" onClick={() => setMobileOpen(false)}>
                  Admin
                </Link>
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
